import { z } from 'zod';
import { membershipSchema } from './organizations';
import { localeSchema, pageSchema, timestampSchema } from './common';
import { permissionSchema } from './permissions';
import { userStatusSchema } from './auth';

export const userSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  status: userStatusSchema,
  locale: localeSchema,
  roles: z.array(z.object({ id: z.string().uuid(), key: z.string(), nameKey: z.string() })),
  /**
   * True when this account holds a platform-scoped role -- the service
   * provider's own login, not one of the client's people.
   *
   * Surfaced rather than hidden. The client is entitled to know which accounts
   * in their organization are not theirs, and the users page needs it anyway:
   * the role and status controls are disabled for such an account, and a row
   * whose buttons are dead for no visible reason is a bug report.
   */
  isPlatformAccount: z.boolean(),
  /** The organization this account belongs to (docs/organizations-design.md). */
  memberOf: membershipSchema,
  lastLoginAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  /** Optimistic concurrency (common.ts): the value {@link updateUserSchema}'s
   * own `version` field must echo back. */
  version: z.number().int().nonnegative(),
});
export type User = z.infer<typeof userSchema>;

export const userPageSchema = pageSchema(userSchema);

export const userListQuerySchema = z.object({
  status: userStatusSchema.optional(),
  /**
   * One organization's members. A caller whose reach is only their own
   * organization (`party.members.read`) is confined to it whether or not they
   * ask, and asking for another answers 403.
   */
  partyId: z.string().uuid().optional(),
  search: z.string().max(120).optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export const updateUserSchema = z
  .object({
    displayName: z.string().min(1).max(200).optional(),
    locale: localeSchema.optional(),
    /**
     * `invited` is deliberately not an accepted value here: it is only ever
     * entered by `inviteUserSchema` and left by claiming the invitation at
     * first login (auth.service.ts). Setting it through this endpoint would
     * leave an already-active identity in a state the login flow does not
     * expect.
     */
    status: z.enum(['active', 'disabled']).optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateUser = z.infer<typeof updateUserSchema>;

/**
 * Admin-initiated invitation: creates the account ahead of first login so its
 * roles can be granted immediately (section 12.2, auth.service.ts case 2).
 * Identity (issuer/subject) is left null and bound only when the invitation
 * is claimed.
 */
export const inviteUserSchema = z
  .object({
    email: z.string().email().max(320),
    displayName: z.string().min(1).max(200),
    locale: localeSchema.default('en'),
    roleIds: z.array(z.string().uuid()).max(20).default([]),
    /**
     * The organization the account joins. Left out, it is the host for an
     * administrator and the caller's own organization for a lead -- who may
     * not name any other (docs/organizations-design.md section 3.5).
     */
    partyId: z.string().uuid().optional(),
  })
  .strict();
export type InviteUser = z.infer<typeof inviteUserSchema>;

/**
 * Moves an account to another organization. Its own endpoint and its own
 * permissions (`MEMBERSHIP_CHANGE_PERMISSIONS`), because it changes which rows
 * every role the person holds applies to -- and it revokes their sessions,
 * like a role change.
 */
export const changeMembershipSchema = z
  .object({
    partyId: z.string().uuid(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type ChangeMembership = z.infer<typeof changeMembershipSchema>;

/**
 * Role assignment is separated from profile editing so it can carry a stricter
 * permission (role.manage) and its own audit action (section 13.3).
 */
export const assignRolesSchema = z.object({ roleIds: z.array(z.string().uuid()).max(20) }).strict();
export type AssignRoles = z.infer<typeof assignRolesSchema>;

/**
 * Self-service profile editing (section 13.3: a normal user may manage their
 * own settings but not their own roles or status). No permission is required
 * -- it is always the caller's own row -- and no `version` is asked for: the
 * profile page always starts from the session's own current values, so the
 * class of "someone else changed this concurrently" conflict the `version`
 * field guards against elsewhere does not apply to editing yourself.
 */
export const updateOwnProfileSchema = z
  .object({
    displayName: z.string().min(1).max(200).optional(),
    locale: localeSchema.optional(),
  })
  .strict();
export type UpdateOwnProfile = z.infer<typeof updateOwnProfileSchema>;

export const roleSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  nameKey: z.string(),
  isSystem: z.boolean(),
  permissions: z.array(permissionSchema),
  memberCount: z.number().int().nonnegative(),
  /** Email-domain rules granting the role. One with any, or with members, cannot be deleted. */
  emailDomainCount: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
});
export type Role = z.infer<typeof roleSchema>;

export const createRoleSchema = z
  .object({
    key: z
      .string()
      .min(2)
      .max(64)
      .regex(/^[a-z0-9][a-z0-9_-]*$/),
    nameKey: z.string().min(1).max(120),
    permissions: z.array(permissionSchema).max(64),
  })
  .strict();
export type CreateRole = z.infer<typeof createRoleSchema>;

export const updateRoleSchema = z
  .object({
    nameKey: z.string().min(1).max(120).optional(),
    permissions: z.array(permissionSchema).max(64).optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateRole = z.infer<typeof updateRoleSchema>;
